import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, resolveImageUrl } from "../App";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Package, User as UserIcon, Heart, LogOut, ChevronRight,
  MapPin, Edit2, Check, X, RefreshCw, ShoppingBag,
  Truck, CheckCircle, Clock, XCircle, Star
} from "lucide-react";
import { toast } from "sonner";

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "orders",   label: "Orders",   icon: Package },
  { id: "profile",  label: "Profile",  icon: UserIcon },
  { id: "wishlist", label: "Wishlist", icon: Heart },
];

// ── Order status config ───────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pending:    { label: "Pending",    color: "bg-amber-100 text-amber-800",   icon: Clock },
  confirmed:  { label: "Confirmed",  color: "bg-blue-100 text-blue-800",     icon: CheckCircle },
  processing: { label: "Processing", color: "bg-purple-100 text-purple-800", icon: RefreshCw },
  shipped:    { label: "Shipped",    color: "bg-indigo-100 text-indigo-800", icon: Truck },
  delivered:  { label: "Delivered",  color: "bg-green-100 text-green-800",   icon: CheckCircle },
  cancelled:  { label: "Cancelled",  color: "bg-red-100 text-red-800",       icon: XCircle },
};

// ── Format date ───────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric", month: "long", year: "numeric",
  });
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg  = STATUS_CONFIG[status] || { label: status, color: "bg-zinc-100 text-zinc-700", icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 font-medium ${cfg.color}`}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

// ── Order progress stepper ────────────────────────────────────────────────────

const STEPS = ["confirmed", "processing", "shipped", "delivered"];

function OrderStepper({ status }) {
  if (status === "cancelled" || status === "pending") return null;
  const currentStep = STEPS.indexOf(status);
  return (
    <div className="flex items-start gap-0 py-4">
      {STEPS.map((step, idx) => {
        const done   = idx <= currentStep;
        const active = idx === currentStep;
        const cfg    = STATUS_CONFIG[step];
        return (
          <div key={step} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                done
                  ? "bg-zinc-900 border-zinc-900 text-white"
                  : "border-zinc-300 text-zinc-300"
              } ${active ? "ring-2 ring-zinc-900 ring-offset-2" : ""}`}>
                {done ? <Check size={11} /> : <span className="text-[10px]">{idx + 1}</span>}
              </div>
              <span className={`text-[9px] uppercase tracking-wide mt-1.5 text-center ${done ? "text-zinc-700" : "text-zinc-400"}`}>
                {cfg.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 mx-1 ${idx < currentStep ? "bg-zinc-900" : "bg-zinc-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AccountPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab]           = useState("orders");
  const [orders, setOrders]                 = useState([]);
  const [wishlist, setWishlist]             = useState([]);
  const [loadingOrders, setLoadingOrders]   = useState(true);
  const [loadingWishlist, setLoadingWishlist] = useState(true);
  const [expandedOrder, setExpandedOrder]   = useState(null);

  // Profile edit
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm]       = useState({
    name:  user?.name  || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchWishlist();
  }, []);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(data);
    } catch (e) { console.error(e); }
    finally { setLoadingOrders(false); }
  };

  const fetchWishlist = async () => {
    try {
      const { data } = await axios.get(`${API}/wishlist`, { withCredentials: true });
      setWishlist(data);
    } catch (e) { console.error(e); }
    finally { setLoadingWishlist(false); }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await axios.put(`${API}/auth/profile`, profileForm, { withCredentials: true });
      setUser(data);
      setEditingProfile(false);
      toast.success("Profile updated!");
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  // Re-order: add all items from a delivered order back to cart
  const reOrder = async (order) => {
    try {
      await Promise.all(
        order.items.map((item) =>
          axios.post(
            `${API}/cart`,
            { product_id: item.product_id, quantity: item.quantity },
            { withCredentials: true }
          )
        )
      );
      toast.success("Items added to cart!");
      navigate("/cart");
    } catch {
      toast.error("Failed to re-order");
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
      setWishlist((prev) => prev.filter((w) => w.product_id !== productId));
      toast.success("Removed from wishlist");
    } catch {
      toast.error("Failed to remove");
    }
  };

  const addWishlistToCart = async (productId) => {
    try {
      await axios.post(`${API}/cart`, { product_id: productId, quantity: 1 }, { withCredentials: true });
      toast.success("Added to cart!");
    } catch {
      toast.error("Failed to add to cart");
    }
  };

  // Computed stats
  const totalSpent = orders.reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <Navbar />

      <div className="flex-1 px-4 md:px-12 max-w-7xl mx-auto w-full py-8 md:py-12">

        {/* ── Page header ───────────────────────────────────────────────── */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">My Account</p>
            <h1 className="cormorant text-4xl font-light tracking-tight" data-testid="account-title">
              {user?.name ? `Welcome, ${user.name.split(" ")[0]}` : "My Account"}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-400 px-4 py-2.5 transition-colors uppercase tracking-widest"
            data-testid="logout-button"
          >
            <LogOut size={13} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>

        {/* ── Stat cards ────────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-3 md:gap-6 mb-10">
          {[
            { label: "Total Orders",   value: orders.length,                              icon: Package },
            { label: "Total Spent",    value: `₹${totalSpent.toLocaleString("en-IN")}`,   icon: ShoppingBag },
            { label: "Wishlist Items", value: wishlist.length,                             icon: Heart },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="bg-white border border-zinc-100 p-4 md:p-6 text-center shadow-sm">
              <Icon size={22} className="mx-auto mb-2 text-[#D4AF37]" />
              <p className="cormorant text-2xl md:text-3xl font-light">{value}</p>
              <p className="text-xs text-zinc-500 mt-0.5 uppercase tracking-wide">{label}</p>
            </div>
          ))}
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────────── */}
        <div className="flex border-b border-zinc-200 mb-8">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest transition-colors border-b-2 -mb-px ${
                activeTab === id
                  ? "border-zinc-900 text-zinc-900 font-medium"
                  : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon size={14} />
              {label}
              {id === "wishlist" && wishlist.length > 0 && (
                <span className="ml-1 bg-zinc-900 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                  {wishlist.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ════════════════════════════════════════════════════════════════
            TAB: ORDERS
        ════════════════════════════════════════════════════════════════ */}
        {activeTab === "orders" && (
          <div>
            {loadingOrders ? (
              <div className="text-center py-16">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent" />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 bg-white border border-zinc-100">
                <Package size={40} className="mx-auto mb-4 text-zinc-300" />
                <p className="cormorant text-2xl font-light mb-2">No orders yet</p>
                <p className="text-sm text-zinc-500 mb-6">Your order history will appear here</p>
                <Link
                  to="/shop"
                  className="inline-block bg-zinc-900 text-white px-8 py-3 text-xs uppercase tracking-widest hover:bg-zinc-800 transition-colors"
                >
                  Start Shopping
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => {
                  const isExpanded = expandedOrder === order.order_id;
                  return (
                    <div
                      key={order.order_id}
                      className="bg-white border border-zinc-100 shadow-sm overflow-hidden"
                      data-testid={`order-${order.order_id}`}
                    >
                      {/* Order header — always visible, click to expand */}
                      <div
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-5 cursor-pointer hover:bg-zinc-50 transition-colors gap-3"
                        onClick={() => setExpandedOrder(isExpanded ? null : order.order_id)}
                      >
                        <div className="flex items-center gap-4">
                          {/* Stacked product thumbnails */}
                          <div className="flex -space-x-2 flex-shrink-0">
                            {order.items?.slice(0, 3).map((item, i) => (
                              <img
                                key={i}
                                src={resolveImageUrl(item.product?.image_url)}
                                alt=""
                                className="w-10 h-10 object-cover border-2 border-white bg-zinc-100"
                              />
                            ))}
                            {order.items?.length > 3 && (
                              <div className="w-10 h-10 bg-zinc-100 border-2 border-white flex items-center justify-center text-xs text-zinc-500">
                                +{order.items.length - 3}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium">
                                Order #{order.order_id.toString().slice(-6).toUpperCase()}
                              </p>
                              <StatusBadge status={order.order_status} />
                            </div>
                            <p className="text-xs text-zinc-500 mt-0.5">
                              {formatDate(order.created_at)} · {order.items?.length} item{order.items?.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="text-lg font-light cormorant">
                            ₹{order.total.toLocaleString("en-IN")}
                          </p>
                          <ChevronRight
                            size={16}
                            className={`text-zinc-400 transition-transform duration-200 flex-shrink-0 ${isExpanded ? "rotate-90" : ""}`}
                          />
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="border-t border-zinc-100 p-5">
                          {/* Progress stepper */}
                          <OrderStepper status={order.order_status} />

                          {/* Items */}
                          <div className="space-y-3 mt-2 mb-5">
                            {order.items?.map((item, idx) => (
                              <Link
                                to={`/product/${item.product_id}`}
                                key={idx}
                                className="flex gap-3 group"
                              >
                                <img
                                  src={resolveImageUrl(item.product?.image_url)}
                                  alt=""
                                  className="w-14 h-14 object-cover bg-zinc-100 flex-shrink-0 group-hover:opacity-80 transition-opacity"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate group-hover:text-zinc-600 transition-colors">
                                    {item.product?.name}
                                  </p>
                                  <p className="text-xs text-zinc-500">Qty: {item.quantity}</p>
                                  <p className="text-xs text-zinc-700">₹{(item.price ?? item.product?.price)?.toLocaleString("en-IN")}</p>
                                </div>
                              </Link>
                            ))}
                          </div>

                          {/* Delivery address */}
                          {order.address && (
                            <div className="bg-zinc-50 border border-zinc-100 p-4 mb-5">
                              <div className="flex items-center gap-2 mb-2">
                                <MapPin size={13} className="text-[#D4AF37]" />
                                <p className="text-xs uppercase tracking-widest text-zinc-600 font-medium">
                                  Delivery Address
                                </p>
                              </div>
                              <p className="text-sm text-zinc-700 leading-relaxed">
                                {order.address.full_name && (
                                  <span className="font-medium">{order.address.full_name}<br /></span>
                                )}
                                {order.address.address_line1 && <span>{order.address.address_line1}<br /></span>}
                                {order.address.address_line2 && <span>{order.address.address_line2}<br /></span>}
                                {[order.address.city, order.address.state, order.address.pincode]
                                  .filter(Boolean).join(", ")}
                              </p>
                            </div>
                          )}

                          {/* Footer row */}
                          <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-zinc-100">
                            <div className="text-xs text-zinc-500 space-y-1">
                              <p>
                                Payment:{" "}
                                <span className="text-zinc-700 capitalize">
                                  {order.payment_method || "Online"}
                                </span>
                              </p>
                              <p>
                                Order total:{" "}
                                <span className="text-zinc-900 font-medium">
                                  ₹{order.total.toLocaleString("en-IN")}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                              {/* Write a review — for delivered orders */}
                              {order.order_status === "delivered" && (
                                <Link
                                  to={`/product/${order.items?.[0]?.product_id}#reviews`}
                                  className="flex items-center gap-1.5 text-xs text-[#D4AF37] hover:text-amber-600 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Star size={12} />
                                  Write a Review
                                </Link>
                              )}
                              {/* Buy Again — for delivered orders */}
                              {order.order_status === "delivered" && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); reOrder(order); }}
                                  className="flex items-center gap-2 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white px-5 py-2.5 text-xs uppercase tracking-widest transition-colors"
                                  data-testid={`reorder-${order.order_id}`}
                                >
                                  <RefreshCw size={12} />
                                  Buy Again
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            TAB: PROFILE
        ════════════════════════════════════════════════════════════════ */}
        {activeTab === "profile" && (
          <div className="max-w-lg">
            <div className="bg-white border border-zinc-100 shadow-sm p-6 md:p-8">
              <div className="flex items-center justify-between mb-8">
                <h2 className="cormorant text-2xl font-light">Personal Details</h2>
                {!editingProfile ? (
                  <button
                    onClick={() => {
                      setProfileForm({
                        name:  user?.name  || "",
                        email: user?.email || "",
                        phone: user?.phone || "",
                      });
                      setEditingProfile(true);
                    }}
                    className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-400 px-3 py-2 transition-colors uppercase tracking-widest"
                    data-testid="edit-profile-btn"
                  >
                    <Edit2 size={12} /> Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={saveProfile}
                      disabled={savingProfile}
                      className="flex items-center gap-1.5 text-xs bg-zinc-900 text-white hover:bg-zinc-800 px-3 py-2 transition-colors uppercase tracking-widest disabled:opacity-50"
                      data-testid="save-profile-btn"
                    >
                      <Check size={12} /> {savingProfile ? "Saving…" : "Save"}
                    </button>
                    <button
                      onClick={() => setEditingProfile(false)}
                      className="flex items-center gap-1.5 text-xs border border-zinc-200 hover:border-zinc-400 px-3 py-2 transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-5">
                {[
                  { label: "Full Name",     field: "name",  type: "text",  placeholder: "Your name" },
                  { label: "Email Address", field: "email", type: "email", placeholder: "your@email.com" },
                  { label: "Phone Number",  field: "phone", type: "tel",   placeholder: "+91 XXXXX XXXXX" },
                ].map(({ label, field, type, placeholder }) => (
                  <div key={field}>
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2 block">
                      {label}
                    </label>
                    {editingProfile ? (
                      <input
                        type={type}
                        value={profileForm[field]}
                        onChange={(e) => setProfileForm({ ...profileForm, [field]: e.target.value })}
                        placeholder={placeholder}
                        className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors bg-white"
                        data-testid={`profile-${field}-input`}
                      />
                    ) : (
                      <p className="text-sm text-zinc-800 py-3 border-b border-zinc-100">
                        {user?.[field] || <span className="text-zinc-400 italic">Not set</span>}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {user?.created_at && (
                <div className="mt-8 pt-6 border-t border-zinc-100">
                  <p className="text-xs text-zinc-400 uppercase tracking-widest">
                    Member since {formatDate(user.created_at)}
                  </p>
                </div>
              )}
            </div>

            {/* Quick links */}
            <div className="mt-4 bg-white border border-zinc-100 shadow-sm divide-y divide-zinc-50">
              <Link
                to="/shop"
                className="flex items-center justify-between px-6 py-4 hover:bg-zinc-50 transition-colors group"
              >
                <span className="text-sm text-zinc-700">Continue Shopping</span>
                <ChevronRight size={14} className="text-zinc-400 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <button
                onClick={() => setActiveTab("wishlist")}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-zinc-50 transition-colors group text-left"
              >
                <span className="text-sm text-zinc-700">My Wishlist</span>
                <ChevronRight size={14} className="text-zinc-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-red-50 transition-colors group text-left"
              >
                <span className="text-sm text-red-500">Sign Out</span>
                <LogOut size={14} className="text-red-400" />
              </button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            TAB: WISHLIST
        ════════════════════════════════════════════════════════════════ */}
        {activeTab === "wishlist" && (
          <div>
            {loadingWishlist ? (
              <div className="text-center py-16">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent" />
              </div>
            ) : wishlist.length === 0 ? (
              <div className="text-center py-16 bg-white border border-zinc-100">
                <Heart size={40} className="mx-auto mb-4 text-zinc-300" />
                <p className="cormorant text-2xl font-light mb-2">Your wishlist is empty</p>
                <p className="text-sm text-zinc-500 mb-6">
                  Save pieces you love and come back to them anytime
                </p>
                <Link
                  to="/shop"
                  className="inline-block bg-zinc-900 text-white px-8 py-3 text-xs uppercase tracking-widest hover:bg-zinc-800 transition-colors"
                >
                  Explore Collection
                </Link>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-zinc-500">
                    {wishlist.length} saved item{wishlist.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {wishlist.map((item) => (
                    <div
                      key={item.product_id}
                      className="group bg-white border border-zinc-100 shadow-sm overflow-hidden"
                      data-testid={`wishlist-${item.product_id}`}
                    >
                      <Link
                        to={`/product/${item.product_id}`}
                        className="block relative aspect-[4/5] overflow-hidden bg-zinc-100"
                      >
                        <img
                          src={resolveImageUrl(item.product?.image_url || item.image_url)}
                          alt={item.product?.name || item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {/* Remove button — appears on hover */}
                        <button
                          onClick={(e) => { e.preventDefault(); removeFromWishlist(item.product_id); }}
                          className="absolute top-2 right-2 w-7 h-7 bg-white/90 hover:bg-white flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove from wishlist"
                        >
                          <X size={13} className="text-zinc-600" />
                        </button>
                      </Link>
                      <div className="p-3">
                        <h3 className="cormorant text-base font-light mb-0.5 truncate">
                          {item.product?.name || item.name}
                        </h3>
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <p className="text-sm font-medium">
                            ₹{(item.product?.price || item.price)?.toLocaleString("en-IN")}
                          </p>
                          <button
                            onClick={() => addWishlistToCart(item.product_id)}
                            className="text-[10px] uppercase tracking-widest bg-zinc-900 text-white px-2.5 py-1.5 hover:bg-zinc-700 transition-colors whitespace-nowrap"
                            data-testid={`wishlist-add-cart-${item.product_id}`}
                          >
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
}